// The watchlist, as this tab holds it (SCENARIOS E5, `GET /api/me/watchlist`).
//
// ONE COPY PER TAB, shared by every star on screen: a star tapped on the card
// fills the same player's star on the Players row behind it, without either
// reading its page again. Kept from each star's answer, which is the sport's
// whole list.
//
// READ AGAIN WHEN THE TAB COMES BACK, and when a screen that lists players
// opens (`fresh`): a star made on another device has to show here (the review
// of 2026-10-03 found a phone's list read once and never again, its empty
// stars standing over the server's fresher answer). Within a tab every star
// goes through this copy, so it needs no clock to know it is current. A read
// that fails is said where the list is shown, with Try again, and is not
// asked again by every row that mounts.
//
// A STAR CHANGES AT ONCE and is put back if the server says no, with the
// server's sentence — a full list says how to make room. A star tapped stands
// over the list until its answer is in the list. A read that was under way
// when a star's answer came back is read again, so an older list never lands
// over a newer star.
//
// Only a real league stars anybody: the tour's simulator has no account to
// keep a list on, and its screens draw no star.

import { useEffect, useSyncExternalStore } from 'react';
import { WatchlistView, type WatchedPlayer } from '@fsp/domain';
import { tell } from '../kit/ui';
import { get, messageOf, send } from './api';
import { useSession } from './session';

interface Held {
  userId: string;
  /** Every sport's list, newest first; null until a read answers. */
  players: WatchedPlayer[] | null;
  /** The last read's failure, while there is no list to show instead. */
  failure: string | null;
  /** Stars tapped and not yet in the list: over the list and over a row's own answer. */
  tapped: ReadonlyMap<string, boolean>;
}

let held: Held | null = null;
const listeners = new Set<() => void>();
/** Bumped by every star the server accepted: a read sent before it is stale. */
let generation = 0;
let reading: string | null = null;
/** Stars on their way to the server: a read that lands meanwhile keeps their taps. */
const inflight = new Set<string>();

function set(next: Held | null): void {
  held = next;
  for (const l of listeners) l();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

function read(userId: string): void {
  if (reading === userId) return;
  reading = userId;
  const sent = generation;
  void get('/me/watchlist', WatchlistView).then((reply) => {
    reading = null;
    if (held?.userId !== userId) return;
    if (reply.status !== 'ok') {
      set({ ...held, failure: messageOf(reply, 'Your watchlist could not be read.') });
      return;
    }
    // A star landed while this was on its way: this list may not have it.
    if (generation !== sent) {
      read(userId);
      return;
    }
    set({
      ...held,
      players: reply.data.players,
      failure: null,
      tapped: new Map([...held.tapped].filter(([id]) => inflight.has(id))),
    });
  });
}

// THE TAB COMES BACK: read again, for whoever is signed in.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && held !== null) read(held.userId);
  });
}

export interface WatchlistRead {
  /** Whether I watch him: a star just tapped, else the list once read, else the row's answer. */
  watching: (playerId: string, fallback?: boolean) => boolean;
  /**
   * Star or unstar. Answers null once the server has it, or the sentence it
   * refused with (already told). Null for nobody signed in.
   */
  toggle: ((playerId: string, on: boolean) => Promise<string | null>) | null;
  /** Every sport's list, newest first; null until read. */
  players: WatchedPlayer[] | null;
  /** Why the list could not be read, while there is none to show. */
  failure: string | null;
  /** Read it again now. */
  retry: () => void;
}

/**
 * The tab's watchlist. `fresh` reads it again as the screen opens — Players
 * and the account page; a star on a row reads it only when nothing has.
 */
export function useWatchlist({ fresh = false }: { fresh?: boolean } = {}): WatchlistRead {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const now = useSyncExternalStore(subscribe, () => held);

  useEffect(() => {
    if (userId === null) {
      if (held !== null) set(null);
      return;
    }
    if (held?.userId !== userId) {
      set({ userId, players: null, failure: null, tapped: new Map() });
    }
    if (fresh || (held?.players === null && held.failure === null)) read(userId);
  }, [userId, fresh]);

  const mine = now !== null && now.userId === userId ? now : null;
  return {
    watching: (playerId, fallback = false) => {
      const tapped = mine?.tapped.get(playerId);
      if (tapped !== undefined) return tapped;
      return mine?.players != null ? mine.players.some((p) => p.player.id === playerId) : fallback;
    },
    toggle:
      userId === null
        ? null
        : async (playerId, on) => {
            const tap = (value: boolean | undefined) => {
              if (held?.userId !== userId) return;
              const tapped = new Map(held.tapped);
              if (value === undefined) tapped.delete(playerId);
              else tapped.set(playerId, value);
              set({ ...held, tapped });
            };
            tap(on);
            inflight.add(playerId);
            const reply = await send(
              on ? 'PUT' : 'DELETE',
              `/me/watchlist/${encodeURIComponent(playerId)}`,
              undefined,
              WatchlistView,
            ).finally(() => inflight.delete(playerId));
            if (held?.userId !== userId) return null;
            if (reply.status !== 'ok') {
              tap(undefined);
              const said = messageOf(
                reply,
                on ? 'Could not star him.' : 'Could not take the star off.',
              );
              tell(said, 'bad');
              return said;
            }
            generation += 1;
            if (held.players === null) {
              // No list yet to put it in: the tap stands until one is read.
              read(userId);
              return null;
            }
            // THE SPORT'S LIST AS IT NOW STANDS, every other sport's kept;
            // the tap is the list's answer now.
            const sport = reply.data.sport;
            const others = held.players.filter((p) => p.sport !== sport);
            const tapped = new Map(held.tapped);
            tapped.delete(playerId);
            set({ ...held, players: [...reply.data.players, ...others], tapped });
            return null;
          },
    players: mine?.players ?? null,
    failure: mine?.players == null ? (mine?.failure ?? null) : null,
    retry: () => {
      if (userId !== null) read(userId);
    },
  };
}
